import React, { useRef } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import FileUpload from '../../../components/ui/FileUpload';
import Icon from '../../../components/AppIcon';
import { capitalizeWords } from '../../../utils/formatters';
import { validateFileUpload } from './FormValidation';

const BankInformationStep = ({ formData, updateFormData, errors, onPreviewDocument, keepExistingDocuments, setKeepExistingDocuments, uploadedFiles }) => {
  const fileInputRef = useRef(null);
  
  // Check if vendor is Indian
  const isIndian = formData.registeredCountry === 'IN';
  
  // Updated account types - Cash Credit Account added for India only
  const accountTypes = [
    { value: 'savings', label: 'Savings Account' },
    { value: 'current', label: 'Current Account' },
    { value: 'od', label: 'Overdraft Account' },
    ...(isIndian ? [{ value: 'cash-credit', label: 'Cash Credit Account' }] : [])
  ];

  const handleInputChange = (field, value) => {
    // Auto-uppercase IFSC and Swift codes
    if (field === 'ifscCode' || field === 'swiftCode') {
      value = value.toUpperCase();
    }
    // Capitalize first letter of every word for specific fields
    const fieldsToCapitalize = ['bankName', 'branchName', 'bankAddress'];
    if (fieldsToCapitalize.includes(field)) {
      value = capitalizeWords(value);
    }
    updateFormData({ [field]: value });
  };

  const handleFileUpload = (field, file) => {
    // FileUpload component now handles validation internally
    updateFormData({ [field]: file });
  };

  const handleRemoveFile = (field) => {
    updateFormData({ [field]: null });
  };

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-2">Bank Information</h2>
        <p className="text-text-secondary">
          Provide your banking details and upload required bank proof documents.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Bank Name"
          type="text"
          placeholder="Enter bank name"
          value={formData.bankName}
          onChange={(e) => handleInputChange('bankName', e.target.value)}
          error={errors.bankName}
          required
        />

        <Input
          label="Branch Name"
          type="text"
          placeholder="Enter branch name"
          value={formData.branchName}
          onChange={(e) => handleInputChange('branchName', e.target.value)}
          error={errors.branchName}
          required={isIndian}
        />

        <Input
          label="Account Number"
          type="text"
          placeholder="Enter account number"
          value={formData.accountNumber}
          onChange={(e) => handleInputChange('accountNumber', e.target.value)}
          error={errors.accountNumber}
          required
        />

        <Input
          label="Confirm Account Number"
          type="text"
          placeholder="Re-enter account number"
          value={formData.confirmAccountNumber}
          onChange={(e) => handleInputChange('confirmAccountNumber', e.target.value)}
          error={errors.confirmAccountNumber}
          required
        />

        {isIndian ? (
          <>
            <Input
              label="IFSC Code"
              type="text"
              placeholder="Enter IFSC code"
              value={formData.ifscCode}
              onChange={(e) => handleInputChange('ifscCode', e.target.value)}
              error={errors.ifscCode}
              required
            />

            <Select
              label="Account Type"
              options={accountTypes}
              value={formData.accountType}
              onChange={(value) => handleInputChange('accountType', value)}
              error={errors.accountType}
              required
            />
          </>
        ) : (
          <>
            <Input
              label="Swift Code"
              type="text"
              placeholder="Enter Swift code (8-11 characters)"
              value={formData.swiftCode}
              onChange={(e) => handleInputChange('swiftCode', e.target.value)}
              error={errors.swiftCode}
              required
              description="Swift code is required for foreign vendors (8-11 characters, letters and numbers only)"
            />

            <Select
              label="Account Type"
              options={accountTypes}
              value={formData.accountType}
              onChange={(value) => handleInputChange('accountType', value)}
              error={errors.accountType}
              required
            />
          </>
        )}

        <div className="md:col-span-2">
          <Input
            label="Bank Address"
            type="text"
            placeholder="Enter complete bank address"
            value={formData.bankAddress}
            onChange={(e) => handleInputChange('bankAddress', e.target.value)}
            error={errors.bankAddress}
            required
          />
        </div>

        {/* Bank Proof Upload */}
        <div className="md:col-span-2 bg-muted/30 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4">Bank Proof Document</h3>
          <p className="text-sm text-text-secondary mb-4">
            {isIndian 
              ? "Upload a cancelled cheque or bank statement (PDF, JPG, PNG - Max 4MB)"
              : "Upload signed and stamped letterhead or official bank document (PDF, JPG, PNG - Max 4MB)"
            }
          </p>
          
          {/* Show checkbox if document is pre-filled */}
          {uploadedFiles?.BANK_PROOF && !formData.bankProof?.name && (
            <div className="mb-4 p-3 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="keepBankProof"
                    checked={keepExistingDocuments.bankProof || false}
                    onChange={(e) => setKeepExistingDocuments(prev => ({ ...prev, bankProof: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="keepBankProof" className="text-sm text-foreground cursor-pointer">
                    Keep existing: <span className="font-medium">{uploadedFiles.BANK_PROOF.file_name}</span>
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => onPreviewDocument && onPreviewDocument(uploadedFiles.BANK_PROOF, 'BANK_PROOF')}
                  className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors flex items-center space-x-1"
                >
                  <Icon name="eye" className="w-4 h-4" />
                  <span>Preview</span>
                </button>
              </div>
            </div>
          )}
          
          {(!keepExistingDocuments.bankProof || !uploadedFiles?.BANK_PROOF) && (
            <FileUpload
            label={
              isIndian
                ? "Cancelled Cheque"
                : "Signed and Stamped letter head of the BANK"
            }
            accept=".pdf,.jpg,.jpeg,.png"
            maxSize={4 * 1024 * 1024}
            value={formData.bankProof}
            onChange={(file) => handleFileUpload('bankProof', file)}
            onRemove={() => handleRemoveFile('bankProof')}
            onPreview={(file) => onPreviewDocument && onPreviewDocument(file, 'BANK_PROOF')}
            error={errors.bankProof}
            required
            description="Max 4MB - PDF, JPG, or PNG format"
          />
          )}
        </div>
      </div>

      {/* Additional Information */}
      {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
            <span className="text-white text-xs font-bold">i</span>
          </div>
          <div>
            <h4 className="font-medium text-blue-900 mb-1">Important Information</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Ensure account number matches exactly with your bank records</li>
              {isIndian ? (
                <li>• IFSC code will be automatically validated</li>
              ) : (
                <li>• Swift code is required and must be 8-11 characters (letters and numbers only)</li>
              )}
              <li>• Bank proof document should be clear and readable</li>
              <li>• All payments will be processed to this account only</li>
            </ul>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default BankInformationStep;